import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { fetchData, formatNumber, formatDate, useMenuIdPermissions, getCategoryNameById, getTargetMeta, formatTargetValue, getEncryptedRoute, toasterrormsg } from '../../../reusable'
import { PageTitle } from '../../../_metronic/layout/core'
import { KTIcon, toAbsoluteUrl } from '../../../_metronic/helpers'
import { Tr } from '../../../_metronic/i18n/Tr'
import { Sponser } from '../types/sponser'
import moment from 'moment'
import { regionList, interestList } from '../types/general'

const STATUS_CONFIG: Record<string, { label: string; class: string }> = {
  DRAFT: { label: 'Draft', class: 'sponser-detail-status--draft' },
  UNDER_REVIEW: { label: 'Under Review', class: 'sponser-detail-status--under_review' },
  APPROVED: { label: 'Approved', class: 'sponser-detail-status--approved' },
  ACTIVATED: { label: 'Active', class: 'sponser-detail-status--activated' },
  REJECTED: { label: 'Rejected', class: 'sponser-detail-status--rejected' },
  COMPLETED: { label: 'Completed', class: 'sponser-detail-status--completed' },
  PAUSED: { label: 'Paused', class: 'sponser-detail-status--paused' },
}


const getSponserScheduleInfo = (startDate: string | null, endDate: string | null, status: string = 'DRAFT') => {
  if (!startDate || !endDate) {
    // Status-only messages when no dates are set
    if (status === 'DRAFT') {
      return {
        icon: 'pencil' as const,
        bgColor: '#F9FAFB',
        borderColor: '#E5E7EB',
        textColor: 'text-muted',
        title: 'Draft',
        message: 'This search ad is still in draft mode and has not been submitted for review yet.',
      }
    }
    if (status === 'UNDER_REVIEW') {
      return {
        icon: 'eye' as const,
        bgColor: '#EFF6FF',
        borderColor: '#BFDBFE',
        textColor: 'text-info',
        title: 'Under Review',
        message: 'This search ad is currently being reviewed by the admin team. You will be notified once a decision is made.',
      }
    }
    if (status === 'REJECTED') {
      return {
        icon: 'cross-circle' as const,
        bgColor: '#FEF2F2',
        borderColor: '#FECACA',
        textColor: 'text-danger',
        title: 'Rejected',
        message: 'This search ad has been rejected. Please review the rejection reason and make the necessary changes before resubmitting.',
      }
    }
    return null
  }

  const today = moment().startOf('day')
  const start = moment(startDate).startOf('day')
  const end = moment(endDate).startOf('day')

  // Status-based messages take priority over date-based logic
  if (status === 'DRAFT') {
    return {
      icon: 'pencil' as const,
      bgColor: '#F9FAFB',
      borderColor: '#E5E7EB',
      textColor: 'text-muted',
      title: 'Draft',
      message: `This search ad is still in draft mode. It is scheduled from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} but has not been submitted yet.`,
    }
  }

  if (status === 'UNDER_REVIEW') {
    return {
      icon: 'eye' as const,
      bgColor: '#EFF6FF',
      borderColor: '#BFDBFE',
      textColor: 'text-info',
      title: 'Under Review',
      message: `This search ad is currently being reviewed. It is scheduled to run from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')}.`,
    }
  }

  if (status === 'REJECTED') {
    return {
      icon: 'cross-circle' as const,
      bgColor: '#FEF2F2',
      borderColor: '#FECACA',
      textColor: 'text-danger',
      title: 'Rejected',
      message: 'This search ad has been rejected. Please review the rejection reason and make the necessary changes before resubmitting.',
    }
  }

  if (status === 'PAUSED') {
    const daysRemaining = end.diff(today, 'days')
    return {
      icon: 'pause-circle' as const,
      bgColor: '#FFFBEB',
      borderColor: '#FDE68A',
      textColor: 'text-warning',
      title: 'Paused',
      message: today.isSameOrBefore(end)
        ? `This search ad is currently paused and not being shown to users. It was scheduled until ${formatDate(endDate, 'DD-MM-YYYY')} (${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining).`
        : `This search ad was paused. Its schedule was from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')}.`,
    }
  }

  if (status === 'COMPLETED') {
    return {
      icon: 'calendar-tick' as const,
      bgColor: '#DBEAFE',
      borderColor: '#93C5FD',
      textColor: 'text-primary',
      title: 'Completed',
      message: `This search ad ran from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} and has ended.`,
    }
  }

  // For APPROVED and ACTIVATED statuses, use date-based logic
  if (status === 'APPROVED') {
    if (today.isBefore(start)) {
      const daysUntilStart = start.diff(today, 'days')
      return {
        icon: 'verify' as const,
        bgColor: '#F0FDF4',
        borderColor: '#BBF7D0',
        textColor: 'text-success',
        title: 'Approved',
        message: `This search ad has been approved and is scheduled to go live from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} (starts in ${daysUntilStart} day${daysUntilStart !== 1 ? 's' : ''}).`,
      }
    }
    return {
      icon: 'verify' as const,
      bgColor: '#F0FDF4',
      borderColor: '#BBF7D0',
      textColor: 'text-success',
      title: 'Approved',
      message: `This search ad has been approved. It is scheduled from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')}.`,
    }
  }

  // ACTIVATED status — date-based logic
  if (today.isBefore(start)) {
    const daysUntilStart = start.diff(today, 'days')
    return {
      icon: 'time' as const,
      bgColor: '#FFF7ED',
      borderColor: '#FED7AA',
      textColor: 'text-warning',
      title: 'Scheduled',
      message: `This search ad is activated and in the queue. It will be shown from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} (starts in ${daysUntilStart} day${daysUntilStart !== 1 ? 's' : ''}).`,
    }
  }
  if (today.isSameOrAfter(start) && today.isSameOrBefore(end)) {
    const daysRemaining = end.diff(today, 'days')
    return {
      icon: 'verify' as const,
      bgColor: '#F0FDF4',
      borderColor: '#BBF7D0',
      textColor: 'text-success',
      title: 'Currently Active',
      message: `This search ad is currently live and being shown to users. It will run until ${formatDate(endDate, 'DD-MM-YYYY')} (${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining).`,
    }
  }
  if (today.isAfter(end)) {
    return {
      icon: 'calendar-tick' as const,
      bgColor: '#F3F4F6',
      borderColor: '#D1D5DB',
      textColor: 'text-muted',
      title: 'Completed',
      message: `This search ad ran from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} and has ended.`,
    }
  }
  return null
}

const DetailRow: FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className = '' }) => (
  <div className={`row py-2 py-sm-3 mx-0 sponser-detail-row ${className}`}>
    <div className='col-12 col-sm-5 col-md-4 sponser-detail-label mb-1 mb-sm-0'>{label}</div>
    <div className='col-12 col-sm-7 col-md-8 sponser-detail-value'>{children}</div>
  </div>
)

const ViewSearchAdvertisementDetail: FC = () => {
  const t = Tr()
  const navigate = useNavigate()
  const { urlData } = useMenuIdPermissions() as any
  const sponserId = urlData?.sponserId || ''

  const [sponserDetail, setSponserDetail] = useState<Sponser | null>(null)
    const [regionList, setRegionList] = useState<regionList[]>([]);
  const [interestList, setInterestList] = useState<interestList[]>([]);

  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [categoryName, setCategoryName] = useState('')
  const [url, setUrl] = useState('')

  const regionListApiCall = async () => {
    try {
      const response = await fetchData('vendor/public/regionList', {})
      if (response.status == '200') {
        const data = response.data
        setRegionList(Array.isArray(data) ? data : []);
      } else {
        console.error(response?.message || 'An error occurred')
        setRegionList([])
      }
    } catch (error) {
      console.error('Error fetching region list:', error)
      setRegionList([])

    }
  }

  const interestListApiCall = async () => {
    try {
      const response = await fetchData('admin/interestlist', {})
      if (response.status == '200') {
        const data = response.data
        setInterestList(Array.isArray(data) ? data : []);
      } else {
        console.error(response?.message || 'An error occurred')
        setInterestList([])
      }
    } catch (error) {
      console.error('Error fetching interest list:', error)
      setInterestList([])
    }
  }


  const sponserDetailsApiCall = async () => {
    try {
      setIsLoading(true)
      setErrorMessage('')
      const res = await fetchData('admin/sponser/detail', { sponserId })
      if (res.status == '200') {
        const data = res?.data
        
        setSponserDetail(data ?? null)
        if (data?.categoryId) {
          const name = await getCategoryNameById(String(data.categoryId))
          setCategoryName(name)
          setUrl(res?.url ?? '')
        }
      } else {
        setErrorMessage(res.message || 'Failed to load sponser details')
      }
    } catch (error) {
      console.error('Error fetching sponser detail:', error)
      setErrorMessage('Something went wrong while fetching sponser details')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    document.title = 'Insayr | Search Advertisement Detail'
    regionListApiCall()
    interestListApiCall()
    if (sponserId) {
      sponserDetailsApiCall()
    } else {
      setIsLoading(false)
      setErrorMessage('there was an issue loading data')
    }
  }, [sponserId])

  if (isLoading) {

    return (
      <div className='d-flex flex-column justify-content-center align-items-center gap-3' style={{ minHeight: 400 }}>
        <span className='spinner-border text-primary' style={{ width: 48, height: 48 }} />
        <p className='sponser-section__subtitle mb-0 fs-6'>{t('Loading...')}</p>
      </div>
    )
  }

  if (errorMessage) {

    return (
      <>
        <PageTitle breadcrumbs={[]}>{t('Search Advertisement Detail')}</PageTitle>
        <div className='d-flex flex-column align-items-center justify-content-center px-3' style={{ minHeight: 400 }}>
          <div className='d-flex align-items-center justify-content-center mb-4' style={{ width: 64, height: 64, borderRadius: 16, background: '#FEE2E2' }}>
            <KTIcon iconName='cross-circle' className='fs-2x text-danger' />
          </div>
          <h4 className='fw-bold text-gray-800 mb-2 text-center'>{t('Error Loading Details')}</h4>
          <p className='text-muted fs-6 text-center mb-5' style={{ maxWidth: 400 }}>{errorMessage}</p>
          <button className='btn btn-light-primary btn-sm d-flex align-items-center gap-2' onClick={() => navigate(-1)}>
            <KTIcon iconName='arrow-left' className='fs-3' /> {t('Go Back')}
          </button>
        </div>
      </>
    )
  }

  if (!sponserDetail) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>{t('Search Advertisement Detail')}</PageTitle>
        <div className='d-flex flex-column align-items-center justify-content-center px-3' style={{ minHeight: 400 }}>
          <div className='d-flex align-items-center justify-content-center mb-4' style={{ width: 64, height: 64, borderRadius: 16, background: '#F3F4F6' }}>
            <KTIcon iconName='document' className='fs-2x text-muted' />
          </div>
          <h4 className='fw-bold text-gray-800 mb-2 text-center'>{t('No Details Found')}</h4>
          <p className='text-muted fs-6 text-center mb-5' style={{ maxWidth: 400 }}>
            {t('The search ad details could not be found. It may have been removed or is no longer available.')}
          </p>
          <button className='btn btn-light-primary btn-sm d-flex align-items-center gap-2' onClick={() => navigate(-1)}>
            <KTIcon iconName='arrow-left' className='fs-3' /> {t('Go Back')}
          </button>
        </div>
      </>
    )
  }

  const status = sponserDetail?.status
  const statusCfg = status && STATUS_CONFIG[status] ? STATUS_CONFIG[status] : { label: 'Not Available', class: 'sponser-detail-status--not-available' }
  const scheduleInfo = sponserDetail?.startDate && getSponserScheduleInfo(sponserDetail?.startDate, sponserDetail?.endDate, status)

  const targets = sponserDetail?.target || []

  return (
    <>
      <PageTitle breadcrumbs={[]}>{t('Search Advertisement Detail')}</PageTitle>

      {/* Header */}
      <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 mb-md-6'>
        <button className='btn btn-light-danger btn-sm d-flex align-items-center gap-2' onClick={() => navigate(-1)}>
          <KTIcon iconName='arrow-left' className='fs-3' /> {t('Back')}
        </button>
        <span className={`sponser-detail-status ${statusCfg.class}`}>{t(statusCfg.label)}</span>
      </div>

      {/* Schedule Status Banner */}
      {scheduleInfo && (
        <div
          className='d-flex flex-column flex-sm-row align-items-start gap-3 p-3 p-sm-4 rounded-3 mb-4 mb-md-6'
          style={{ background: scheduleInfo.bgColor, border: `1px solid ${scheduleInfo.borderColor}` }}
        >
          <KTIcon iconName={scheduleInfo.icon} className={`fs-1 ${scheduleInfo.textColor} flex-shrink-0`} />
          <div className='min-w-0'>
            <div className={`fw-bold fs-6 ${scheduleInfo.textColor} mb-1`}>{t(scheduleInfo.title)}</div>
            <p className='text-gray-700 fs-7 mb-0'>{scheduleInfo.message}</p>
          </div>
        </div>
      )}

      {/* Reject Reason */}
      {status === 'REJECTED' && sponserDetail?.rejectReason && (
        <div className='sponser-reject-reason mb-4 mb-md-6'>
          <KTIcon iconName='information-5' className='fs-2 text-danger mt-1 flex-shrink-0' />
          <p className='sponser-reject-reason__text mb-0'>
            <strong>{t('Rejection Reason:')}</strong> {sponserDetail?.rejectReason}
          </p>
        </div>
      )}

      <div className='row g-4 g-xl-6'>
        {/* LEFT COLUMN */}
        <div className='col-12 col-lg-8'>

          {/* Vendor Info */}
          <div className='sponser-section'>
            <div className='sponser-section__header'>
              <div className='sponser-section__icon sponser-section__icon--teal flex-shrink-0'>
                <KTIcon iconName='profile-user' className='fs-2 text-info' />
              </div>
              <div className='min-w-0'>
                <h4 className='sponser-section__title'>{t('Vendor Information')}</h4>
                <p className='sponser-section__subtitle mb-0'>{t('Details about the vendor')}</p>
              </div>
            </div>
            <div className='d-flex flex-column flex-sm-row align-items-center align-items-sm-start gap-3 p-3 p-sm-4 rounded-3 bg-light border'>
              {sponserDetail?.logo && (
                <img src={url + "vendor/" + sponserDetail?.logo} alt='Vendor' className='sponser-vendor-avatar flex-shrink-0' onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = toAbsoluteUrl('/media/logos/mainLogo.png')
                }} />
              )}
              <div className='text-center text-sm-start min-w-0 w-100'>
                <div className='fw-bold fs-5 text-dark text-truncate'>{sponserDetail?.fullName || '-'}</div>
                {sponserDetail?.email && <div className='text-muted fs-7 text-truncate'>{sponserDetail?.email}</div>}
                {sponserDetail?.mobileNumber && <div className='text-muted fs-7'>{sponserDetail?.mobileNumber}</div>}
                {sponserDetail?.categoryId && (
                  <span className='badge badge-light-primary mt-2 text-truncate d-inline-block' style={{ maxWidth: '100%' }}>
                    {categoryName || sponserDetail?.categoryId}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Placement */}
          <div className='sponser-section'>
            <div className='sponser-section__header'>
              <div className='sponser-section__icon sponser-section__icon--blue flex-shrink-0'>
                <KTIcon iconName='geolocation-home' className='fs-2 text-primary' />
              </div>
              <div className='min-w-0'>
                <h4 className='sponser-section__title'>{t('Promoted Property')}</h4>
                <p className='sponser-section__subtitle mb-0'>{t('The property being promoted')}</p>
              </div>
            </div>
            <div className='d-flex flex-column flex-sm-row align-items-center align-items-sm-start gap-3 p-3 p-sm-4 rounded-3 bg-light border'>
              <div className='sponser-placement-card__icon flex-shrink-0'>
                <KTIcon iconName='abstract-26' className='fs-1 text-primary' />
              </div>
              <div className='min-w-0 text-center text-sm-start flex-grow-1'>
                {sponserDetail?.en_parentName ? (
                  <>
                    <div className='fw-bold fs-5 text-dark text-break'>{sponserDetail.en_parentName}</div>
                    <div className='d-flex align-items-center gap-2 mt-2 ps-3' style={{ borderLeft: '2px solid #6366F1' }}>
                      <KTIcon iconName='arrow-right' className='fs-6 text-primary flex-shrink-0' />
                      <span className='fw-semibold fs-6 text-primary text-break'>{sponserDetail.en_name}</span>
                    </div>
                  </>
                ) : (
                  <div className='fw-bold fs-5 text-dark text-break'>{sponserDetail?.en_name || '-'}</div>
                )}
              </div>
              {sponserDetail?.amountPerDay && (
                <div className='flex-shrink-0 text-center text-sm-end'>
                  <span className='fw-bolder fs-3 text-dark d-block'>{formatNumber(parseFloat(sponserDetail?.amountPerDay))}</span>
                  <span className='text-muted fs-8 fw-semibold'>{t('per day')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Targeting */}
          <div className='sponser-section'>
            <div className='sponser-section__header'>
              <div className='sponser-section__icon sponser-section__icon--purple flex-shrink-0'>
                <KTIcon iconName='focus' className='fs-2 text-purple' />
              </div>
              <div className='min-w-0'>
                <h4 className='sponser-section__title'>{t('Targeting Settings')}</h4>
                <p className='sponser-section__subtitle mb-0'>{t('Audience filters applied to this search ad')}</p>
              </div>
            </div>
            {targets.length > 0 && targets[0]?.sponserstargetId !== null ? (
              <div className='row g-3'>
                {targets.map((target: any, idx: number) => {
                  const meta = getTargetMeta(target?.key)
                  const targetIcon = target?.iconName || meta.icon
                  const colorClass = meta.colorClass
                  const displayValue = formatTargetValue(target?.key, target?.value, regionList, interestList)

                  return (
                    <div className='col-6 col-sm-4 col-md-3' key={idx}>
                      <div className='sponser-target-card text-center p-3 rounded-3 h-100'>
                        <div className={`d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-light-${colorClass}`} style={{ width: 40, height: 40 }}>
                          <KTIcon iconName={targetIcon} className={`fs-3 text-${colorClass}`} />
                        </div>
                        <div className='text-muted fw-bold fs-9 text-uppercase mb-1'>{target.key || '-'}</div>
                        <div className='fw-bold fs-7 text-dark text-break'>{displayValue}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className='d-flex align-items-start gap-2 text-muted'>
                <KTIcon iconName='information-5' className='fs-3 flex-shrink-0 mt-1' />
                <span className='fs-6'>{t('No specific targeting — search ad will be shown to all users')}</span>
              </div>
            )}
          </div>

          {/* Sponsored Properties */}
          {(sponserDetail?.actionDetail && Array.isArray(sponserDetail.actionDetail) && sponserDetail.actionDetail.length > 0) && (
            <div className='sponser-section'>
              <div className='sponser-section__header'>
                <div className='sponser-section__icon sponser-section__icon--green flex-shrink-0'>
                  <KTIcon iconName='home-2' className='fs-2 text-success' />
                </div>
                <div className='min-w-0'>
                  <h4 className='sponser-section__title'>{t('Sponsored Properties')}</h4>
                  <p className='sponser-section__subtitle mb-0'>{t('Properties and items promoted with this search ad')}</p>
                </div>
              </div>
              <div className='row g-3'>
                {sponserDetail.actionDetail.map((item: any, idx: number) => (
                  <div className='col-6 col-sm-4 col-md-3' key={idx}>
                    <div className='text-center p-3 rounded-3 h-100' style={{ border: '1px solid #e9ecef', background: '#fafafa' }}>
                      <div className='d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-light-primary' style={{ width: 40, height: 40 }}>
                        <KTIcon iconName={item?.en_parentName ? 'home-2' : 'shop'} className='fs-3 text-primary' />
                      </div>
                      {item?.en_parentName && (
                        <div className='text-muted fw-bold fs-9 text-uppercase mb-1'>{item.en_parentName}</div>
                      )}
                      <div className='fw-bold fs-7 text-dark text-break'>{item?.en_name || item?.actionId || '-'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div className='col-12 col-lg-4 '>

          {/* Ad Summary */}
          <div className='sponser-section'>
            <div className='sponser-section__header'>
              <div className='sponser-section__icon sponser-section__icon--blue flex-shrink-0'>
                <KTIcon iconName='clipboard' className='fs-2 text-primary' />
              </div>
              <div className='min-w-0'>
                <h4 className='sponser-section__title'>{t('Search Ad Summary')}</h4>
              </div>
            </div>
            <DetailRow label={t('Search Ad Name')}>
              <span className='fw-bold text-break'>{sponserDetail?.en_name || '-'}</span>
            </DetailRow>
            <DetailRow label={t('Status')}>
              <span className={`sponser-detail-status ${statusCfg.class}`}>{t(statusCfg.label)}</span>
            </DetailRow>
            <DetailRow label={t('Duration')}>
              <span className='text-nowrap'>{sponserDetail?.duration ? `${sponserDetail?.duration} ${sponserDetail?.durationType}` : '-'}</span>
            </DetailRow>
            <DetailRow label={t('Created')}>
              <span className='text-nowrap'>{sponserDetail?.created ? formatDate(sponserDetail?.created, 'DD-MM-YYYY') : '-'}</span>
            </DetailRow>
            {sponserDetail?.startDate && (
              <DetailRow label={t('Start Date')}>
                <span className='text-nowrap'>{formatDate(sponserDetail?.startDate, 'DD-MM-YYYY')}</span>
              </DetailRow>
            )}
            {sponserDetail?.endDate && (
              <DetailRow label={t('End Date')}>
                <span className='text-nowrap'>{formatDate(sponserDetail?.endDate, 'DD-MM-YYYY')}</span>
              </DetailRow>
            )}
          </div>

          {/* Budget & Payment */}
          <div className='sponser-section sponser-right-col'>
            <div className='sponser-section__header'>
              <div className='sponser-section__icon sponser-section__icon--green flex-shrink-0'>
                <KTIcon iconName='dollar' className='fs-2 text-success' />
              </div>
              <div className='min-w-0'>
                <h4 className='sponser-section__title'>{t('Budget & Payment')}</h4>
                <p className='sponser-section__subtitle mb-0'>{t('Billing and payment details')}</p>
              </div>
            </div>
            <div className='sponser-payment-summary'>
              <div className='sponser-payment-row'>
                <span className='sponser-payment-row__label flex-shrink-0'>{t('Placement')}</span>
                <span className='sponser-payment-row__value text-end text-break ms-2'>{sponserDetail?.placementName || '-'}</span>
              </div>
              <div className='sponser-payment-row'>
                <span className='sponser-payment-row__label flex-shrink-0'>{t('Pricing Model')}</span>
                <span className='sponser-payment-row__value text-end'>{t('Per Day')}</span>
              </div>
              <div className='sponser-payment-row'>
                <span className='sponser-payment-row__label flex-shrink-0'>{t('Duration')}</span>
                <span className='sponser-payment-row__value text-end text-nowrap'>{sponserDetail?.duration ? `${sponserDetail?.duration} ${sponserDetail?.durationType}` : '-'}</span>
              </div>
              <div className='sponser-payment-row'>
                <span className='sponser-payment-row__label flex-shrink-0'>{t('Rate Per Day')}</span>
                <span className='sponser-payment-row__value text-end text-nowrap'>{sponserDetail?.perDayPrice ? formatNumber(parseFloat(sponserDetail?.perDayPrice)) : '-'}</span>
              </div>
              <div className='sponser-payment-row sponser-payment-row--total'>
                <span className='sponser-payment-row__label flex-shrink-0'>{t('Total Amount')}</span>
                <span className='sponser-payment-row__value text-end text-nowrap'>{sponserDetail?.price ? formatNumber(parseFloat(sponserDetail?.price)) : '0 UZS'}</span>
              </div>
            </div>

            {sponserDetail?.paymentMethod && (
              <div className='mt-4'>
                <div className='sponser-detail-label mb-2'>{t('Payment Method')}</div>
                <div className='d-flex align-items-center gap-3 p-3 rounded-3' style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                  {sponserDetail?.paymentIcon && (
                    <img src={sponserDetail?.paymentIcon} alt='Payment' className='flex-shrink-0' style={{ width: 36, height: 36, objectFit: 'contain' }} />
                  )}
                  <div className='min-w-0'>
                    <div className='fw-bold text-success text-truncate'>{sponserDetail?.paymentMethod}</div>
                    {sponserDetail?.paymentStatus && <span className='fs-8 text-muted'>{sponserDetail?.paymentStatus}</span>}
                  </div>
                </div>
              </div>
            )}

            {(sponserDetail?.transactionNumber || sponserDetail?.transactionId) && (
              <DetailRow label={t('Transaction ID')}>
                <code className='fs-7 text-break d-inline-block' style={{ maxWidth: '100%' }}>
                  {sponserDetail?.transactionNumber || sponserDetail?.transactionId}
                </code>
              </DetailRow>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export { ViewSearchAdvertisementDetail }
